import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CartItemOnlineFulfillmentEnum,
  ExtendedLocationPackageItem,
  FilterSubType,
  FulfillmentPreference,
  ProductItem as ProductItemType,
  ShopCartStep,
  ShopCartType,
  ShopPromoResponseType,
  UserFulfillmentMode
} from 'types';

export type CartStore = {
  isLoading: boolean;
  errorMsg: string;
  continueShoppingUrl: string;
  cartStep: ShopCartStep;
  selectedPackages: ExtendedLocationPackageItem[];
  selectedProducts: ProductItemType[];
  selectedBrandId: number | null;
  selectedCategoryId: number | null;
  currentFilter: string;
  selectedFilterType: FilterSubType;
  promoCodes: ShopPromoResponseType[];
  fulfillmentPreferences: {
    [cart_item_id: number]: FulfillmentPreference;
  };
  isPreferencesMenuEnabled: boolean;
  currentInvoiceId: number | null;
};

const initialState: CartStore = {
  isLoading: false,
  errorMsg: '',
  continueShoppingUrl: '',
  cartStep: ShopCartStep.DETAILS,
  selectedPackages: [],
  selectedProducts: [],
  selectedBrandId: 0,
  selectedCategoryId: 0,
  currentFilter: '',
  selectedFilterType: FilterSubType.CATEGORY,
  promoCodes: [],
  fulfillmentPreferences: {},
  isPreferencesMenuEnabled: true,
  currentInvoiceId: null
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.errorMsg = action.payload;
    },
    setContinueShoppingUrl: (state, action: PayloadAction<string>) => {
      state.continueShoppingUrl = action.payload;
    },
    addToLocalCart: (
      state,
      action: PayloadAction<{
        type: ShopCartType;
        item: ExtendedLocationPackageItem | ProductItemType;
      }>
    ) => {
      switch (action.payload.type) {
        case ShopCartType.Package:
          state.selectedPackages = [
            ...(state.selectedPackages ?? []),
            action.payload.item as ExtendedLocationPackageItem
          ];
          break;
        case ShopCartType.Product:
          state.selectedProducts = [
            ...(state.selectedProducts ?? []),
            action.payload.item as ProductItemType
          ];
          break;
        default:
          break;
      }
    },
    removeFromLocalCart: (
      state,
      action: PayloadAction<{ type: ShopCartType; id: number }>
    ) => {
      switch (action.payload.type) {
        case ShopCartType.Package:
          state.selectedPackages = (state.selectedPackages ?? []).filter(
            (item) => item.id !== action.payload.id
          );
          break;
        case ShopCartType.Product:
          state.selectedProducts = (state.selectedProducts ?? []).filter(
            (item) => item.id !== action.payload.id
          );
          break;
        default:
          break;
      }
      // Also remove fulfillment preference for this item (use cart_item_id = id for local cart)
      delete state.fulfillmentPreferences[action.payload.id];
    },
    updateLocalCartQuantity: (
      state,
      action: PayloadAction<{
        type: ShopCartType;
        id: number;
        qty: number;
      }>
    ) => {
      switch (action.payload.type) {
        case ShopCartType.Package:
          state.selectedPackages = state.selectedPackages.map((item) => {
            if (item.id === action.payload?.id) {
              return {
                ...item,
                qty: action.payload?.qty
              };
            }

            return item;
          });
          break;
        case ShopCartType.Product:
          state.selectedProducts = state.selectedProducts.map((item) => {
            if (item.id === action.payload?.id) {
              return {
                ...item,
                qty: action.payload?.qty
              };
            }

            return item;
          });
          break;
        default:
          break;
      }
    },
    clearCart: (state) => {
      state.selectedPackages = [];
      state.selectedProducts = [];
    },
    clearCartPackages: (state) => {
      state.selectedPackages = [];
    },
    setCartStep: (state, action: PayloadAction<number>) => {
      state.cartStep = action.payload;
    },
    selectFilterType: (state, action: PayloadAction<FilterSubType>) => {
      state.selectedFilterType = action.payload;
    },
    selectCurrentFilter: (state, action: PayloadAction<string>) => {
      state.currentFilter = action.payload;
    },
    selectCurrentBrandId: (state, action: PayloadAction<number | null>) => {
      state.selectedBrandId = action.payload;
    },
    selectCurrentCategoryId: (state, action: PayloadAction<number | null>) => {
      state.selectedCategoryId = action.payload;
    },
    clearShopFilters: (state) => {
      state.selectedFilterType = FilterSubType.CATEGORY;
      state.currentFilter = '';
      state.selectedCategoryId = 0;
      state.selectedBrandId = 0;
    },
    setPromoDetails: (state, action: PayloadAction<ShopPromoResponseType>) => {
      const isExist = (state.promoCodes ?? []).some(
        (promo) => promo.promo_id === action.payload.promo_id
      );

      state.promoCodes = isExist
        ? state.promoCodes
        : [...(state.promoCodes ?? []), action.payload];
    },
    removePromoDetails: (state, action: PayloadAction<number>) => {
      state.promoCodes = (state.promoCodes ?? []).filter(
        (item) => item.promo_id !== action.payload
      );
    },
    clearPromoDetails: (state) => {
      state.promoCodes = [];
    },

    // Fulfillment preference management
    setFulfillmentPreference: (
      state,
      action: PayloadAction<{
        cart_item_id: number;
        original_fulfillment: CartItemOnlineFulfillmentEnum;
        user_selected: UserFulfillmentMode;
        invoice_id: number;
      }>
    ) => {
      const { cart_item_id, original_fulfillment, user_selected, invoice_id } =
        action.payload;
      // Clear preferences if switching to different invoice
      if (
        state.currentInvoiceId !== null &&
        state.currentInvoiceId !== invoice_id
      ) {
        state.fulfillmentPreferences = {};
      }

      state.currentInvoiceId = invoice_id;
      state.fulfillmentPreferences[cart_item_id] = {
        cart_item_id,
        original_fulfillment,
        user_selected,
        timestamp: Date.now()
      };
    },
    clearFulfillmentPreference: (state, action: PayloadAction<number>) => {
      delete state.fulfillmentPreferences[action.payload];
    },
    clearAllFulfillmentPreferences: (state) => {
      state.fulfillmentPreferences = {};
      state.currentInvoiceId = null;
    },
    cleanupStalePreferences: (
      state,
      action: PayloadAction<{
        current_cart_item_ids: number[];
        invoice_id: number;
      }>
    ) => {
      const { current_cart_item_ids, invoice_id } = action.payload;
      if (state.currentInvoiceId === invoice_id) {
        Object.keys(state.fulfillmentPreferences).forEach((cartItemIdStr) => {
          const cartItemId = parseInt(cartItemIdStr, 10);
          if (!current_cart_item_ids.includes(cartItemId)) {
            delete state.fulfillmentPreferences[cartItemId];
          }
        });
      }
    },
    setIsPreferencesMenuEnabled: (state, action: PayloadAction<boolean>) => {
      state.isPreferencesMenuEnabled = action.payload;
    }
  }
});

export const {
  setIsLoading,
  setError,
  setContinueShoppingUrl,
  setCartStep,
  setPromoDetails,
  removePromoDetails,
  clearPromoDetails,
  addToLocalCart,
  removeFromLocalCart,
  updateLocalCartQuantity,
  clearCart,
  clearCartPackages,
  selectCurrentBrandId,
  selectCurrentCategoryId,
  selectFilterType,
  selectCurrentFilter,
  clearShopFilters,
  setFulfillmentPreference,
  clearFulfillmentPreference,
  clearAllFulfillmentPreferences,
  cleanupStalePreferences,
  setIsPreferencesMenuEnabled
} = cartSlice.actions;

export default cartSlice.reducer;
